import {
	FormItem,
	Select,
	SelectItem,
	TextInput,
	ValidationError }
	 from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditVmwareHorizonAppViewModel,
	SaveVmwareHorizonAppAppModel,
	ApplicationKind,
	ClientKind,
	saveVmwareHorizonAppApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditVmwareHorizonAppProps = {
	initialSettings: EditAppSettings<EditVmwareHorizonAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditVmwareHorizonApp = forwardRef(
	(
		props: EditVmwareHorizonAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveVmwareHorizonAppAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveVmwareHorizonAppAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveVmwareHorizonAppAppModel): SaveVmwareHorizonAppAppModel => {
				return {
					...model,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveVmwareHorizonAppAppModel>(
			formHandler,
			saveVmwareHorizonAppApp,
			AppType.VmwareHorizonApp,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"portalUrl",
				"applicationName",
				"applicationKind",
				"clientKind"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem
					label={localize("_OmnissaHorizonPortalUrl")}
					isRequired
				>
					<TextInput
						{...formHandler.for("portalUrl")
							.attachToTextInput()
						}
					/>
					<ValidationError
						{...formHandler
							.for("portalUrl")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_OmnissaApplicationName")}
					isRequired
				>
					<TextInput
						{...formHandler.for("applicationName")
							.attachToTextInput()
						}
					/>
					<ValidationError
						{...formHandler
							.for("applicationName")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_AppType")}
					isRequired
				>
					<Select
						{...formHandler.for("applicationKind")
							.attachToTextInput()
						}
						options={appKindOptions}
						menuTitle={localize("_AppType")}
					/>
					<ValidationError
						{...formHandler
							.for("applicationKind")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_ClientType")}
					isRequired
				>
					<Select
						{...formHandler.for("clientKind")
							.attachToTextInput()
						}
						options={clientKindOptions}
						menuTitle={localize("_ClientType")}
					/>
					<ValidationError
						{...formHandler
							.for("clientKind")
							.attachToValidationError()
						}
					/>
				</FormItem>
			</EditAppBase>
		);
	}
);

EditVmwareHorizonApp.displayName = "EditVmwareHorizonApp";

const validation = createValidationConfiguration<SaveVmwareHorizonAppAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());

validation.forScalar("portalUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		})
	);

validation.forScalar("applicationName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("applicationKind")
	.add(
		isNotNullNorUndefined()
	);

validation.forScalar("clientKind")
	.add(
		isNotNullNorUndefined()
	);

const appKindOptions: readonly SelectItem<ApplicationKind>[] = [
	{ text: localize("_Application"), value: ApplicationKind.Application },
	{ text: localize("_Desktop"), value: ApplicationKind.Desktop }
];

const clientKindOptions: readonly SelectItem<ClientKind>[] = [
	{ text: localize("_Web"), value: ClientKind.Web },
	{ text: localize("_NativeClientWeb"), value: ClientKind.NativeViaWeb },
	{ text: localize("_NativeClientProtocolHandler"), value: ClientKind.Native }
];

function convertToSaveModel(
	initialSettings: EditAppSettings<EditVmwareHorizonAppViewModel>
): SaveVmwareHorizonAppAppModel {
	const {
		maintenanceWindow,
		conditionalAccessSettings,
		applicationKind,
		applicationName,
		clientKind,
		portalUrl
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings),
		applicationKind,
		applicationName,
		clientKind,
		portalUrl
	};
}

